import { ActionContext } from 'vuex';
import { notify } from '@kyvg/vue3-notification';

import { notificationErrorText } from '@/utils/PortalConstants';
import store from '@/store';
import {
  ModuleNotificationLoaderData,
  ModuleNotifications,
  ModuleNotificationsCountersData,
  ModuleNotificationsData,
  ModuleNotificationsItemsData,
  ModuleNotificationsState,
  State,
} from '@/models/main';
import {
  getNotificationsAllSorted,
  getNotificationsUnreadCount,
  getTicketsNotificationsUnreadCount,
  setNotificationAsReadRequest,
  setNotificationAsUnreadRequest,
  setNotificationsAllAsReadRequest,
  setTicketNotificationAsReadRequest,
} from '@/api';

export const moduleNotifications = {
  namespaced: true,
  state: {
    notifications: {
      counter_unread: 0,
      counter_tickets_unread: 0,
      unread_filter: true,
      category: 'all',
      loading: false,
      loadingIds: [],
      items: [],
      error: null,
      items_all: {},
      items_shown: [],
      counter_showed: 10,
    },
  },
  getters: {
    notifications: (state: ModuleNotificationsState): ModuleNotificationsData =>
      state.notifications,
  },
  mutations: {
    SET_NOTIFICATIONS_ALL_DATA(
      state: ModuleNotificationsState,
      data: ModuleNotificationsItemsData,
    ): void {
      state.notifications.items_all = data;
    },
    SET_NOTIFICATIONS_COUNTERS(
      state: ModuleNotificationsState,
      data: ModuleNotificationsCountersData,
    ): void {
      state.notifications.counter_unread = data.counter_unread;
      state.notifications.counter_tickets_unread = data.counter_tickets_unread;
    },
    SET_NOTIFICATION_AS_READ(
      state: ModuleNotificationsState,
      id: number,
    ): void {
      // All items + category
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (state.notifications?.items_all.all != null) {
        const notification = state.notifications.items_all.all.find(
          (x) => x.id === id,
        );
        if (notification != null) {
          notification.unread = false;
          const notificationInCategory = state.notifications.items_all[
            notification.category
          ].find((x) => x.id === id);
          if (notificationInCategory != null) {
            notificationInCategory.unread = false;
          }
        }
        // Shown items
        const notificationInShown = state.notifications.items_shown.find(
          (x) => x.id === id,
        );
        if (notificationInShown != null) {
          notificationInShown.unread = false;
        }
      }
    },
    SET_TICKET_NOTIFICATION_AS_READ(
      state: ModuleNotificationsState,
      id: number,
    ): void {
      // All items + category
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (state.notifications?.items_all.all != null) {
        const notification = state.notifications.items_all.all.find(
          (x) => x.source_target_id === String(id),
        );
        if (notification != null) {
          notification.unread = false;
          const notificationInCategory = state.notifications.items_all[
            notification.category
          ].find((x) => x.source_target_id === String(id));
          if (notificationInCategory != null) {
            notificationInCategory.unread = false;
          }
        }
        // Shown items
        const notificationInShown = state.notifications.items_shown.find(
          (x) => x.source_target_id === String(id),
        );
        if (notificationInShown != null) {
          notificationInShown.unread = false;
        }
      }
    },
    SET_NOTIFICATION_AS_UNREAD(
      state: ModuleNotificationsState,
      id: number,
    ): void {
      // All items + category
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (state.notifications?.items_all.all != null) {
        const notification = state.notifications.items_all.all.find(
          (x) => x.id === id,
        );
        if (notification != null) {
          notification.unread = true;
          const notificationInCategory = state.notifications.items_all[
            notification.category
          ].find((x) => x.id === id);
          if (notificationInCategory != null) {
            notificationInCategory.unread = true;
          }
        }
        // Shown items
        const notificationInShown = state.notifications.items_shown.find(
          (x) => x.id === id,
        );
        if (notificationInShown != null) {
          notificationInShown.unread = true;
        }
      }
    },
    SET_LOADER(state: ModuleNotificationsState, value: boolean): void {
      state.notifications.loading = value;
    },
    SET_LOADER_ID(
      state: ModuleNotificationsState,
      data: ModuleNotificationLoaderData,
    ): void {
      const idx = state.notifications.loadingIds.indexOf(data.id);
      if (data.loading && idx === -1) {
        state.notifications.loadingIds.push(data.id);
      } else {
        state.notifications.loadingIds.splice(idx, 1);
      }
    },
    SET_ERROR(state: ModuleNotificationsState, error: string | null): void {
      state.notifications.error = error;
    },
    SET_EMPTY_ITEMS(state: ModuleNotificationsState): void {
      state.notifications.items_shown = [];
      state.notifications.counter_showed = 10;
    },
    SET_NOTIFICATIONS_UNREAD_FILTER(
      state: ModuleNotificationsState,
      unreadFilter: boolean,
    ): void {
      state.notifications.unread_filter = unreadFilter;
      state.notifications.items_shown = [];
      state.notifications.counter_showed = 10;
    },
    SET_NOTIFICATIONS_CATEGORY(
      state: ModuleNotificationsState,
      category: string,
    ): void {
      if (Object.keys(state.notifications.items_all).includes(category)) {
        state.notifications.category = category;
      }
      state.notifications.items_shown = [];
      state.notifications.counter_showed = 10;
    },
    SET_NOTIFICATIONS_NEXT_PAGE(state: ModuleNotificationsState): void {
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (state.notifications?.items_all != null) {
        let categoryItems: ModuleNotifications[] = [];
        if (
          // TODO
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          state.notifications.items_all[state.notifications.category] != null
        ) {
          categoryItems =
            state.notifications.items_all[state.notifications.category];
        }

        if (
          state.notifications.unread_filter &&
          !(categoryItems.length === 0)
        ) {
          categoryItems = [...categoryItems.filter((x) => x.unread)];
        }

        const slicedItems = categoryItems.slice(
          state.notifications.counter_showed - 10,
          state.notifications.counter_showed,
        );

        slicedItems.map((item) => state.notifications.items_shown.push(item));

        if (
          state.notifications.counter_showed <=
          state.notifications.items_shown.length
        ) {
          state.notifications.counter_showed += 10;
        } else {
          state.notifications.counter_showed += slicedItems.length;
        }
      }
    },
  },
  actions: {
    //
    // Counters
    async getNotificationsUnreadCountWrapper(): Promise<number> {
      return await getNotificationsUnreadCount();
    },
    async getTicketsNotificationsUnreadCountWrapper(): Promise<number> {
      return await getTicketsNotificationsUnreadCount();
    },
    // Update notifications counters: all / unread
    async updateNotificationsCounters({
      commit,
    }: ActionContext<ModuleNotificationsState, State>): Promise<void> {
      const unreadCounter = await store.dispatch(
        'moduleNotifications/getNotificationsUnreadCountWrapper',
      );
      const unreadTicketsCounter = await store.dispatch(
        'moduleNotifications/getTicketsNotificationsUnreadCountWrapper',
      );
      const data = {
        counter_unread: unreadCounter,
        counter_tickets_unread: unreadTicketsCounter,
      };
      commit('SET_NOTIFICATIONS_COUNTERS', data);
    },
    //
    // Get all notifications
    async getNotificationsAll({
      commit,
    }: ActionContext<ModuleNotificationsState, State>): // page: number,
    Promise<void> {
      // Loader & clean up
      commit('SET_LOADER', true);
      commit('SET_EMPTY_ITEMS');

      // Get counter values
      const unreadCounter = await getNotificationsUnreadCount().catch(() => {
        commit('SET_ERROR', "Can't get notifications counters");
        commit('SET_LOADER', false);
      });
      const unreadTicketsCounter =
        await getTicketsNotificationsUnreadCount().catch(() => {
          commit('SET_ERROR', "Can't get tickets notifications counters");
        });
      // Get notifications
      const notificationItems = await getNotificationsAllSorted().catch(() => {
        notify({
          group: 'defaultNotificationHolder',
          title: 'Error getting notifications',
          text: notificationErrorText,
          type: 'warning',
        });
        commit('SET_ERROR', "Can't get notifications");
        commit('SET_LOADER', false);
      });
      // Set counters
      commit('SET_NOTIFICATIONS_COUNTERS', {
        counter_unread: unreadCounter,
        counter_tickets_unread: unreadTicketsCounter,
      });
      // Set data and open current page
      commit('SET_NOTIFICATIONS_ALL_DATA', notificationItems);
      commit('SET_NOTIFICATIONS_NEXT_PAGE');
      // Loader
      commit('SET_LOADER', false);
    },
    //
    // SINGLE AS READ
    async setNotificationAsRead(
      { commit }: ActionContext<ModuleNotificationsState, State>,
      id: number,
    ): Promise<void> {
      // SET_LOADER_ID
      commit('SET_LOADER_ID', {
        id,
        loading: true,
      });
      setNotificationAsReadRequest(id)
        .then(() => {
          commit('SET_NOTIFICATION_AS_READ', id);
          void store.dispatch(
            'moduleNotifications/updateNotificationsCounters',
          );
        })
        .catch(() => {
          notify({
            group: 'defaultNotificationHolder',
            title: 'Error setting the notification as read',
            text: notificationErrorText,
            type: 'warning',
          });
        })
        .finally(() => {
          commit('SET_LOADER_ID', {
            id,
            loading: false,
          });
        });
    },
    //
    // SINGLE AS UNREAD
    async setNotificationAsUnread(
      { commit }: ActionContext<ModuleNotificationsState, State>,
      id: number,
    ): Promise<void> {
      // SET_LOADER_ID
      commit('SET_LOADER_ID', {
        id,
        loading: true,
      });
      setNotificationAsUnreadRequest(id)
        .then(async () => {
          commit('SET_NOTIFICATION_AS_UNREAD', id);
          void store.dispatch(
            'moduleNotifications/updateNotificationsCounters',
          );
        })
        .catch(() => {
          notify({
            group: 'defaultNotificationHolder',
            title: 'Error setting the notification as unread',
            text: notificationErrorText,
            type: 'warning',
          });
        })
        .finally(() => {
          commit('SET_LOADER_ID', {
            id,
            loading: false,
          });
        });
    },
    //
    // ALL AS READ
    setNotificationsAllAsRead({
      commit,
    }: ActionContext<ModuleNotificationsState, State>): void {
      // SET_LOADER
      commit('SET_LOADER', true);
      commit('SET_EMPTY_ITEMS');
      //
      setNotificationsAllAsReadRequest()
        .then(async () => {
          await store.dispatch('moduleNotifications/getNotificationsAll');
          await store.dispatch(
            'moduleNotifications/updateNotificationsCounters',
          );
        })
        .catch(() => {
          notify({
            group: 'defaultNotificationHolder',
            title: 'Error setting all the notifications as read',
            text: notificationErrorText,
            type: 'warning',
          });
        })
        .finally(() => {
          commit('SET_LOADER', false);
        });
    },
    //
    // SET TICKETS NOTIFICATIONS AS READ
    async setTicketNotificationsAsRead(
      { commit }: ActionContext<ModuleNotificationsState, State>,
      id: number,
    ): Promise<void> {
      // SET_LOADER_ID
      commit('SET_LOADER_ID', {
        id,
        loading: true,
      });
      setTicketNotificationAsReadRequest(id)
        .then(() => {
          commit('SET_TICKET_NOTIFICATION_AS_READ', id);
          void store.dispatch(
            'moduleNotifications/updateNotificationsCounters',
          );
        })
        .catch(() => {
          notify({
            group: 'defaultNotificationHolder',
            title: 'Error setting the notification as read',
            text: notificationErrorText,
            type: 'warning',
          });
        })
        .finally(() => {
          commit('SET_LOADER_ID', {
            id,
            loading: false,
          });
        });
    },
    //
    // SET NOTIFICATIONS UNREAD FILTER
    async setNotificationsUnreadFilter(
      { commit }: ActionContext<ModuleNotificationsState, State>,
      unreadFilter: boolean,
    ): Promise<void> {
      commit('SET_NOTIFICATIONS_UNREAD_FILTER', unreadFilter);
      commit('SET_NOTIFICATIONS_NEXT_PAGE');
    },
    //
    // SET NOTIFICATIONS CATEGORY TAB
    setNotificationsCategory(
      { commit }: ActionContext<ModuleNotificationsState, State>,
      category: string,
    ) {
      commit('SET_NOTIFICATIONS_CATEGORY', category);
      commit('SET_NOTIFICATIONS_NEXT_PAGE');
    },
    //
    async getNextPage({
      commit,
    }: ActionContext<ModuleNotificationsState, State>): Promise<void> {
      commit('SET_NOTIFICATIONS_NEXT_PAGE');
    },
  },
};
